import React, { useState, useEffect } from 'react';
import { 
  Plus, Search, Phone, MessageCircle, Building2, DollarSign, Calendar, 
  ChevronRight, User, Flame, Thermometer, Snowflake, AlertTriangle, 
  BarChart2, Wand2, GripVertical, ArrowRight, Eye
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription } from '@/components/ui/dialog';
import { useNavigate } from 'react-router-dom';
import api from '@/utils/api';
import useApiData from '@/hooks/useApiData';
import { toast } from 'sonner';
import { formatCurrency, formatDate } from '@/utils/helpers';
import LeadDetailModal from '@/components/LeadDetailModal';
import MessageTemplatesModal from '@/components/MessageTemplatesModal';

const LEAD_COLUMNS = [
  { id: 'lead',              title: 'Lead',             color: 'bg-slate-500',  light: 'bg-slate-50  border-slate-200' },
  { id: 'primeiro_contato',  title: 'Primeiro Contato', color: 'bg-blue-500',   light: 'bg-blue-50   border-blue-200'  },
  { id: 'proposta_enviada',  title: 'Proposta Enviada', color: 'bg-yellow-500', light: 'bg-yellow-50 border-yellow-200'},
  { id: 'negociacao',        title: 'Negociação',       color: 'bg-purple-500', light: 'bg-purple-50 border-purple-200'},
  { id: 'fechado',           title: 'Fechado',          color: 'bg-green-500',  light: 'bg-green-50  border-green-200' },
  { id: 'perdido',           title: 'Perdido',          color: 'bg-red-500',    light: 'bg-red-50    border-red-200'   },
];

const ORIGENS = ['Google Ads', 'Instagram', 'Facebook', 'LinkedIn', 'Indicação', 'Site', 'Networking', 'Outro'];
const SEGMENTOS = ['Tecnologia', 'Saúde', 'Alimentação', 'Moda', 'Construção', 'Educação', 'Serviços', 'Varejo', 'Outro'];

const TEMP_CONFIG = {
  quente: { icon: Flame,       color: 'text-red-500',   label: '🔥' },
  morno:  { icon: Thermometer, color: 'text-amber-500', label: '🌡️' },
  frio:   { icon: Snowflake,   color: 'text-blue-500',  label: '❄️' },
};

function isOverdue(dateStr) {
  if (!dateStr) return false;
  return new Date(dateStr) < new Date();
}

function openWhatsApp(phone) {
  if (!phone) { toast.error('Lead sem telefone cadastrado'); return; }
  const n = phone.replace(/\D/g, '');
  window.open(`https://wa.me/${n.startsWith('55') ? n : '55' + n}`, '_blank');
}

function LeadCard({ lead, onClick, onUpdate }) {
  const overdue = isOverdue(lead.proxima_acao) && !['fechado', 'perdido'].includes(lead.status);
  const tempCfg = TEMP_CONFIG[lead.temperatura];

  return (
    <div
      className={`bg-white rounded-xl p-3.5 shadow-sm border transition-all cursor-pointer hover:shadow-md hover:border-violet-200 group relative
        ${overdue ? 'border-red-200 ring-1 ring-red-100' : 'border-slate-100'}`}
      draggable
      onDragStart={e => { e.dataTransfer.effectAllowed = 'move'; e.dataTransfer.setData('leadId', lead.id); }}
      onClick={() => onClick(lead)}
    >
      {/* Temperatura badge */}
      {lead.temperatura && (
        <div className="absolute top-2.5 right-2.5 text-xs" title={tempCfg?.label}>
          {tempCfg?.label}
        </div>
      )}

      {/* Alerta de atraso */}
      {overdue && (
        <div className="flex items-center gap-1 mb-2 text-[10px] text-red-600 bg-red-50 px-2 py-0.5 rounded-full w-fit">
          <AlertTriangle className="h-3 w-3" /> Ação atrasada
        </div>
      )}

      <h4 className="font-semibold text-slate-900 text-sm leading-snug pr-6 mb-2 line-clamp-2">{lead.title}</h4>

      {lead.company_name && (
        <div className="flex items-center gap-1.5 text-xs text-slate-500 mb-1">
          <Building2 className="h-3 w-3 flex-shrink-0" />
          <span className="truncate">{lead.company_name}</span>
        </div>
      )}

      {lead.contact_name && (
        <div className="flex items-center gap-1.5 text-xs text-slate-500 mb-1">
          <User className="h-3 w-3 flex-shrink-0" />
          <span className="truncate">{lead.contact_name}</span>
        </div>
      )}

      {lead.valor_estimado > 0 && (
        <div className="flex items-center gap-1.5 text-xs font-semibold text-violet-600 mt-2">
          <DollarSign className="h-3 w-3" />
          <span>{formatCurrency(lead.valor_estimado)}</span>
        </div>
      )}

      {lead.proxima_acao && (
        <div className={`flex items-center gap-1.5 text-xs mt-1.5 ${overdue ? 'text-red-500' : 'text-slate-400'}`}>
          <Calendar className="h-3 w-3" />
          <span>{formatDate(lead.proxima_acao)}</span>
        </div>
      )}

      {lead.proxima_acao_descricao && (
        <p className="text-[11px] text-slate-500 mt-1 italic truncate">{lead.proxima_acao_descricao}</p>
      )}

      {/* Ações rápidas */}
      <div className="flex gap-1 mt-3 opacity-0 group-hover:opacity-100 transition-opacity" onClick={e => e.stopPropagation()}>
        {lead.contact_phone && (
          <button
            className="flex-1 flex items-center justify-center gap-1 text-[11px] font-medium py-1 rounded-lg bg-green-50 text-green-700 border border-green-200 hover:bg-green-100 transition-colors"
            onClick={() => openWhatsApp(lead.contact_phone)}
          >
            <MessageCircle className="h-3 w-3" /> WhatsApp
          </button>
        )}
        {lead.contact_phone && (
          <button
            className="flex-1 flex items-center justify-center gap-1 text-[11px] font-medium py-1 rounded-lg bg-slate-50 text-slate-600 border border-slate-200 hover:bg-slate-100 transition-colors"
            onClick={() => window.location.href = `tel:${lead.contact_phone}`}
          >
            <Phone className="h-3 w-3" /> Ligar
          </button>
        )}
        <button
          className="flex items-center justify-center gap-1 text-[11px] font-medium py-1 px-2 rounded-lg bg-violet-50 text-violet-600 border border-violet-200 hover:bg-violet-100 transition-colors"
          onClick={() => onClick(lead)}
        >
          <Eye className="h-3 w-3" />
        </button>
      </div>
    </div>
  );
}

export default function CRMLeads() {
  const { data: leadsData, refresh: refreshLeads } = useApiData('/leads');
  const leads = leadsData || [];
  const navigate = useNavigate();

  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedLead, setSelectedLead] = useState(null);
  const [showTemplates, setShowTemplates] = useState(false);
  const [templateLead, setTemplateLead] = useState(null);

  const [formData, setFormData] = useState({
    title: '', contact_name: '', contact_email: '', contact_phone: '',
    company_name: '', valor_estimado: '', origem: '', segmento: '',
    responsavel_id: '', proxima_acao: '', previsao_fechamento: '', notes: '', temperatura: 'morno'
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const payload = {
        ...formData,
        valor_estimado: parseFloat(formData.valor_estimado) || 0,
        proxima_acao: formData.proxima_acao ? new Date(formData.proxima_acao).toISOString() : null,
        previsao_fechamento: formData.previsao_fechamento ? new Date(formData.previsao_fechamento).toISOString() : null
      };
      await api.post('/leads', payload);
      toast.success('Lead criado com sucesso!');
      setIsDialogOpen(false);
      resetForm();
      refreshLeads();
    } catch (error) {
      toast.error('Erro ao criar lead');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => setFormData({
    title: '', contact_name: '', contact_email: '', contact_phone: '',
    company_name: '', valor_estimado: '', origem: '', segmento: '',
    responsavel_id: '', proxima_acao: '', previsao_fechamento: '', notes: '', temperatura: 'morno'
  });

  // Drag & Drop
  const handleDragOver = (e) => { e.preventDefault(); e.dataTransfer.dropEffect = 'move'; };
  const handleDrop = async (e, newStatus) => {
    e.preventDefault();
    const leadId = e.dataTransfer.getData('leadId');
    const lead = leads.find(l => l.id === leadId);
    if (!lead || lead.status === newStatus) return;
    try {
      await api.put(`/leads/${leadId}`, { status: newStatus });
      toast.success('Etapa atualizada!');
      refreshLeads();
    } catch (error) {
      toast.error('Erro ao atualizar status');
    }
  };

  const handleConvertLead = async (leadId) => {
    if (!window.confirm('Converter este lead em Cliente + Contrato + Projeto?')) return;
    try {
      await api.post(`/leads/${leadId}/convert`);
      toast.success('Lead convertido com sucesso!');
      refreshLeads();
    } catch (error) {
      toast.error('Erro ao converter lead');
    }
  };

  const filteredLeads = leads.filter(lead => {
    if (!searchTerm) return true;
    const q = searchTerm.toLowerCase();
    return (
      lead.title?.toLowerCase().includes(q) ||
      lead.contact_name?.toLowerCase().includes(q) ||
      lead.company_name?.toLowerCase().includes(q)
    );
  });

  const getLeadsByStatus = (status) => filteredLeads.filter(l => l.status === status);

  // Métricas
  const totalLeads = leads.length;
  const leadsAbertos = leads.filter(l => !['fechado', 'perdido'].includes(l.status)).length;
  const leadsFechados = leads.filter(l => l.status === 'fechado').length;
  const valorPipeline = leads.filter(l => !['fechado', 'perdido'].includes(l.status)).reduce((s, l) => s + (l.valor_estimado || 0), 0);
  const taxaConversao = totalLeads > 0 ? ((leadsFechados / totalLeads) * 100).toFixed(1) : 0;
  const leadsAtrasados = leads.filter(l => isOverdue(l.proxima_acao) && !['fechado', 'perdido'].includes(l.status)).length;

  const handleCardClick = (lead) => {
    const freshLead = leads.find(l => l.id === lead.id) || lead;
    setSelectedLead(freshLead);
  };

  return (
    <div className="space-y-5" data-testid="crm-leads-page">
      {/* Header */}
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-4xl font-heading font-bold tracking-tight text-slate-900">CRM de Leads</h1>
          <p className="text-slate-500 mt-1">Pipeline de vendas e prospecção</p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            className="gap-1.5 border-violet-200 text-violet-700 hover:bg-violet-50"
            onClick={() => navigate('/crm/performance')}
          >
            <BarChart2 className="h-4 w-4" /> Performance
          </Button>
          <Button
            variant="outline"
            size="sm"
            className="gap-1.5 border-green-200 text-green-700 hover:bg-green-50"
            onClick={() => { setTemplateLead(null); setShowTemplates(true); }}
          >
            <Wand2 className="h-4 w-4" /> Templates
          </Button>
          <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
            <DialogTrigger asChild>
              <Button className="bg-primary hover:bg-primary/90 gap-1.5" data-testid="new-lead-btn">
                <Plus className="h-4 w-4" /> Novo Lead
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto premium-scrollbar">
              <DialogHeader>
                <DialogTitle>Novo Lead</DialogTitle>
                <DialogDescription>Cadastre um novo lead no pipeline</DialogDescription>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <Label>Título/Oportunidade *</Label>
                  <Input value={formData.title} onChange={e => setFormData({ ...formData, title: e.target.value })}
                    placeholder="Ex: Website para empresa X" required className="mt-1" />
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label>Nome do Contato</Label>
                    <Input value={formData.contact_name} onChange={e => setFormData({ ...formData, contact_name: e.target.value })} className="mt-1" />
                  </div>
                  <div>
                    <Label>Empresa</Label>
                    <Input value={formData.company_name} onChange={e => setFormData({ ...formData, company_name: e.target.value })} className="mt-1" />
                  </div>
                  <div>
                    <Label>Email</Label>
                    <Input type="email" value={formData.contact_email} onChange={e => setFormData({ ...formData, contact_email: e.target.value })} className="mt-1" />
                  </div>
                  <div>
                    <Label>Telefone/WhatsApp</Label>
                    <Input value={formData.contact_phone} onChange={e => setFormData({ ...formData, contact_phone: e.target.value })} className="mt-1" placeholder="(11) 99999-9999" />
                  </div>
                </div>

                <div className="grid grid-cols-3 gap-3">
                  <div>
                    <Label>Valor Estimado (R$)</Label>
                    <Input type="number" step="0.01" value={formData.valor_estimado}
                      onChange={e => setFormData({ ...formData, valor_estimado: e.target.value })} className="mt-1" />
                  </div>
                  <div>
                    <Label>Temperatura</Label>
                    <Select value={formData.temperatura} onValueChange={v => setFormData({ ...formData, temperatura: v })}>
                      <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="quente">🔥 Quente</SelectItem>
                        <SelectItem value="morno">🌡️ Morno</SelectItem>
                        <SelectItem value="frio">❄️ Frio</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Origem</Label>
                    <Select value={formData.origem} onValueChange={v => setFormData({ ...formData, origem: v })}>
                      <SelectTrigger className="mt-1"><SelectValue placeholder="Selecione" /></SelectTrigger>
                      <SelectContent>{ORIGENS.map(o => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label>Próxima Ação (Descrição)</Label>
                    <Input value={formData.proxima_acao_descricao || ''}
                      onChange={e => setFormData({ ...formData, proxima_acao_descricao: e.target.value })}
                      placeholder="Ex: Enviar proposta amanhã" className="mt-1" />
                  </div>
                  <div>
                    <Label>Data da Próxima Ação</Label>
                    <Input type="date" value={formData.proxima_acao}
                      onChange={e => setFormData({ ...formData, proxima_acao: e.target.value })} className="mt-1" />
                  </div>
                </div>

                <div>
                  <Label>Observações</Label>
                  <textarea
                    className="w-full mt-1 min-h-[70px] px-3 py-2 text-sm rounded-md border border-input bg-transparent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/20 focus-visible:border-primary"
                    value={formData.notes} onChange={e => setFormData({ ...formData, notes: e.target.value })}
                  />
                </div>

                <div className="flex justify-end gap-3">
                  <Button type="button" variant="outline" onClick={() => setIsDialogOpen(false)}>Cancelar</Button>
                  <Button type="submit" disabled={loading}>{loading ? 'Salvando...' : 'Criar Lead'}</Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Alertas inteligentes */}
      {leadsAtrasados > 0 && (
        <div className="flex items-center gap-3 px-4 py-3 bg-amber-50 border border-amber-200 rounded-xl">
          <AlertTriangle className="h-4 w-4 text-amber-600 flex-shrink-0" />
          <p className="text-sm text-amber-700 font-medium">
            Você tem <strong>{leadsAtrasados}</strong> lead{leadsAtrasados > 1 ? 's' : ''} com ação atrasada. Atualize o pipeline!
          </p>
        </div>
      )}

      {/* Métricas rápidas */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        {[
          { label: 'Total Leads', value: totalLeads, color: 'text-slate-700' },
          { label: 'Em Aberto', value: leadsAbertos, color: 'text-blue-600' },
          { label: 'Fechados', value: leadsFechados, color: 'text-green-600' },
          { label: 'Valor Pipeline', value: formatCurrency(valorPipeline), color: 'text-violet-600' },
          { label: 'Conversão', value: `${taxaConversao}%`, color: 'text-emerald-600' },
        ].map(m => (
          <div key={m.label} className="bg-white p-3.5 rounded-xl border border-slate-100 shadow-sm">
            <p className="text-xs text-slate-500 uppercase tracking-wide">{m.label}</p>
            <p className={`text-xl font-bold ${m.color} mt-0.5`}>{m.value}</p>
          </div>
        ))}
      </div>

      {/* Filtros */}
      <div className="flex gap-3">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <Input placeholder="Buscar leads..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)} className="pl-9" />
        </div>
      </div>

      {/* Kanban Board */}
      <div className="flex gap-3 overflow-x-auto pb-4 premium-scrollbar">
        {LEAD_COLUMNS.map(column => {
          const colLeads = getLeadsByStatus(column.id);
          const colValue = colLeads.reduce((s, l) => s + (l.valor_estimado || 0), 0);
          return (
            <div
              key={column.id}
              className="flex-shrink-0 w-72 bg-slate-50/80 rounded-2xl p-3 border border-slate-100"
              onDragOver={handleDragOver}
              onDrop={e => handleDrop(e, column.id)}
            >
              {/* Column header */}
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <div className={`w-2.5 h-2.5 rounded-full ${column.color}`} />
                  <h3 className="font-semibold text-slate-800 text-sm">{column.title}</h3>
                </div>
                <div className="flex items-center gap-1.5">
                  <Badge variant="secondary" className="text-xs h-5">{colLeads.length}</Badge>
                </div>
              </div>
              {colValue > 0 && (
                <p className="text-xs text-slate-400 mb-2 font-medium">{formatCurrency(colValue)}</p>
              )}

              <div className="space-y-2.5 min-h-[120px]">
                {colLeads.map(lead => (
                  <LeadCard
                    key={lead.id}
                    lead={lead}
                    onClick={handleCardClick}
                    onUpdate={refreshLeads}
                  />
                ))}

                {/* Converter button em negociação */}
                {column.id === 'negociacao' && colLeads.length > 0 && false && (
                  <div />
                )}

                {colLeads.length === 0 && (
                  <div className="text-center py-8 text-slate-400 text-sm border-2 border-dashed border-slate-200 rounded-xl">
                    Solte leads aqui
                  </div>
                )}
              </div>

              {/* Convert buttons por lead na coluna negociação */}
              {column.id === 'negociacao' && colLeads.map(lead => (
                <button
                  key={`conv-${lead.id}`}
                  className="w-full mt-1 text-xs py-1 text-green-700 border border-green-200 bg-green-50 rounded-lg hover:bg-green-100 transition-colors flex items-center justify-center gap-1"
                  onClick={() => handleConvertLead(lead.id)}
                >
                  <ChevronRight className="h-3 w-3" /> {lead.title.length > 18 ? lead.title.slice(0, 18) + '...' : lead.title} → Converter
                </button>
              ))}
            </div>
          );
        })}
      </div>

      {/* Lead Detail Modal */}
      {selectedLead && (
        <LeadDetailModal
          lead={selectedLead}
          onClose={() => setSelectedLead(null)}
          onUpdate={() => { refreshLeads(); setSelectedLead(null); }}
        />
      )}

      {/* Message Templates Modal */}
      <MessageTemplatesModal
        open={showTemplates}
        onClose={() => setShowTemplates(false)}
        lead={templateLead}
      />
    </div>
  );
}
